import { config } from '../config';

// transcribeAudio sends a recorded audio blob to the per-org transcribe
// endpoint and returns the recognised text. Mirrors the multipart shape
// the Go handler at internal/api/transcribe.go expects.
//
// `language` is an ISO 639-1 hint (e.g. "sv", "en"). The SPA passes its
// active i18n locale; Whisper auto-detects when omitted but a hint
// improves accuracy on short clips. The server caps audio at ~10MB and
// the SPA caps recording at 30 seconds, so a normal clip is well under.
//
// Errors map to typed error codes the AI command bar maps to inline
// hints: "empty_audio" (no speech), "transcribe_unavailable" (sidecar
// not configured — SPA hides the mic button), "transcribe_failed"
// (network / sidecar error), "too_large" (recording cap exceeded).

export type TranscribeErrorCode =
  | 'transcribe_unavailable'
  | 'empty_audio'
  | 'invalid_input'
  | 'too_large'
  | 'transcribe_failed'
  | 'unknown';

export class TranscribeError extends Error {
  code: TranscribeErrorCode;
  constructor(code: TranscribeErrorCode, message?: string) {
    super(message ?? code);
    this.code = code;
  }
}

interface TranscribeResponse {
  text: string;
}

export async function transcribeAudio(
  token: string | null,
  slug: string,
  audio: Blob,
  language: string,
): Promise<string> {
  const form = new FormData();
  form.append('audio', audio, 'audio.webm');
  if (language) form.append('language', language);

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(
      `${config.apiURL}/api/v1/organizations/${encodeURIComponent(slug)}/transcribe`,
      { method: 'POST', headers, body: form },
    );
  } catch {
    // Network failure — distinct from a server error so the SPA can
    // tell the user "couldn't reach the server" if we ever surface
    // that detail. For now both fold into transcribe_failed.
    throw new TranscribeError('transcribe_failed', 'network');
  }

  if (!res.ok) {
    let code: TranscribeErrorCode = 'unknown';
    try {
      const body = (await res.json()) as { error?: string };
      const known: TranscribeErrorCode[] = [
        'transcribe_unavailable',
        'empty_audio',
        'invalid_input',
        'too_large',
        'transcribe_failed',
      ];
      if (body.error && (known as string[]).includes(body.error)) {
        code = body.error as TranscribeErrorCode;
      }
    } catch {
      // non-JSON body — keep code as 'unknown'
    }
    throw new TranscribeError(code);
  }

  const out = (await res.json()) as TranscribeResponse;
  return out.text;
}
